import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { api, ApiError, newIdempotencyKey } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { currencyName } from "@/lib/currency";
import { savePostAuthTarget } from "@/lib/postAuthRedirect";
import "@/styles/sales-landing.css";

/**
 * /passes — sales landing v2 (Manuel's prototype, ported from the design
 * repo prelaunch/sales.jsx). One job: sell passes.
 *
 * Live-data adaptations over the static prototype:
 *   - Prices come from GET /api/v1/pass-catalog (current tier + September
 *     step-up + October full price per product), not hardcoded.
 *   - Issuer-based USD/MXN payment choice (Stripe MX: Mexican cards must be
 *     charged in MXN; American cards in USD). Persisted in localStorage.
 *   - Buy buttons run the real funnel: auth gate (savePostAuthTarget →
 *     /login) → POST /api/v1/checkout/session → Stripe redirect.
 *   - Bilingual EN/ES from the prototype's T dict (tot26-lang persisted).
 */

export const Route = createFileRoute("/passes")({
  component: SalesLanding,
});

/* ---------------------------------------------------------------- data */

interface Tier {
  id: string; code: string; price_cents: number; currency: string;
  starts_at: string | null; ends_at: string | null;
}
interface Product {
  id: string; name: string; inventory_remaining: number;
  current_tier: Tier | null; next_tier: Tier | null; all_tiers: Tier[];
}
interface CatalogResp {
  currency: string;
  available_currencies: string[];
  products: Product[];
}

type PassKey = "day" | "week" | "full";
const PRODUCT_KEY: Record<string, PassKey> = {
  "Day Pass": "day",
  "Weekly Pass": "week",
  "Full Festival Pass": "full",
};

const STEP_UP = new Date("2026-09-01T00:00:00-05:00");
const WEEK_C = ["var(--c26-week1)", "var(--c26-week2)", "var(--c26-week3)", "var(--c26-week4)"];

/* ---------------------------------------------------------------- i18n */

const T = {
  en: {
    lang: "ES", langTitle: "Ver en español",
    bannerOffer: "August: 50% off every pass", bannerStep: "Prices step up September 1",
    navBuy: "Buy your pass",
    heroEyebrow: "Oct 28 – Nov 27, 2026 · Tulum, México",
    h1: "Four weeks of Tulum, one pass",
    script: "come, celebrate & savor",
    lede: "Thirty-one days of dinners, ceremonies and welcome drinks across the best venues in Tulum. Your phone is the pass — no wristband, nothing to print.",
    from: "From",
    fromNote: "50% off every pass through August 31",
    buyNow: "Buy your pass now", seeIncluded: "What you get",
    trust: "Secure checkout by Stripe · Apple Pay & Google Pay · Your QR pass arrives by email",
    inclEyebrow: "Your pass", inclTitle: "Your account is your pass",
    incl: [
      "The full month's schedule, updated live",
      "Welcome drinks, appetizers and desserts at partner venues",
      "Admission to ceremonies and special dinners",
      "Member discounts across participating restaurants and bars",
    ],
    passEyebrow: "Get your pass", passTitle: "Three passes. One month of Tulum.",
    currencyEyebrow: "Choose how to pay",
    currencyTitle: "Pay in the currency of the bank that issued your card.",
    usdBank: "Is your card issued by an American bank?",
    usdAction: "Pay in dollars · USD",
    mxnBank: "Is your card issued by a Mexican bank?",
    mxnAction: "Pay in pesos · MXN",
    currencyHelp: "Use the country of your card issuer, not your current location. Prices below update to the selected payment currency.",
    names: { day: "Day pass", week: "Week pass", full: "Full festival" } as Record<PassKey, string>,
    scope: { day: "Any single day", week: "One themed week", full: "All four weeks" } as Record<PassKey, string>,
    desc: {
      day: "Pick your day once the schedule locks. Full access to that day's offers.",
      week: "Seven days of one themed week — dinners, sessions and workshops included.",
      full: "Everything, all month. Access to all four themed weeks.",
    } as Record<PassKey, string>,
    buy: { day: "Buy day pass", week: "Buy week pass", full: "Buy full festival" } as Record<PassKey, string>,
    buying: "Redirecting…", signInBuy: "Sign in & buy", soldOut: "Sold out",
    bestValue: "Best value",
    octPrice: "October price", fromSep: "from September 1",
    passNote: "The August rate is 50% off the October price and it steps up on September 1. Your pass covers the festival dates whenever you buy it.",
    ladderEyebrow: "The price only goes up",
    ladder: [
      { mo: "August", off: "50% off every pass", now: true },
      { mo: "September", off: "25% off every pass", now: false },
      { mo: "October", off: "Full price", now: false },
    ],
    nowTag: "Now",
    ladderNote: "Buy today and you keep the August rate. Payment is once — there is nothing to renew.",
    weeksEyebrow: "Four weeks, four themes",
    weeks: [
      { t: "A celebration of spirits", d: "Oct 28 – Nov 3" },
      { t: "Authentic Mexican cuisine", d: "Nov 4 – Nov 10" },
      { t: "International delights", d: "Nov 11 – Nov 17" },
      { t: "Gratitude around the world", d: "Nov 18 – Nov 27" },
    ],
    week: "Week ",
    closerTitle: "Lock the August rate",
    closerLede: "50% off every pass ends August 31. After that the same pass costs 25% more.",
    stickyT: "50% off ends Aug 31", stickyCta: "Buy",
    stickyFrom: "From",
    footFine: "Eat With Me presents Taste of Tulum, Food & Wine Festival · Tulum, Quintana Roo, México",
    footCurrency: "Prices are shown in the payment currency selected above.",
    heroAlt: "La Catrina crowned with marigolds above a Tulum shoreline at dusk",
    panAlt: "Lantern-lit Tulum restaurant patios at dusk",
    left: "left",
    payFailed: "Checkout failed. Please try again.",
  },
  es: {
    lang: "EN", langTitle: "View in English",
    bannerOffer: "Agosto: 50% de descuento en todos los pases", bannerStep: "Los precios suben el 1 de septiembre",
    navBuy: "Compra tu pase",
    heroEyebrow: "28 oct – 27 nov, 2026 · Tulum, México",
    h1: "Cuatro semanas de Tulum, un solo pase",
    script: "ven, celebra y saborea",
    lede: "Treinta y un días de cenas, ceremonias y bebidas de bienvenida en las mejores sedes de Tulum. Tu teléfono es el pase: sin pulsera y sin nada que imprimir.",
    from: "Desde",
    fromNote: "50% de descuento en todos los pases hasta el 31 de agosto",
    buyNow: "Compra tu pase", seeIncluded: "Qué incluye",
    trust: "Pago seguro con Stripe · Apple Pay y Google Pay · Tu pase con código QR llega por correo",
    inclEyebrow: "Tu pase", inclTitle: "Tu cuenta es tu pase",
    incl: [
      "El calendario del mes completo, actualizado al día",
      "Bebidas de bienvenida, entradas y postres en las sedes participantes",
      "Entrada a ceremonias y cenas especiales",
      "Descuentos para miembros en restaurantes y bares participantes",
    ],
    passEyebrow: "Consigue tu pase", passTitle: "Tres pases. Un mes de Tulum.",
    currencyEyebrow: "Elige cómo pagar",
    currencyTitle: "Paga en la moneda del banco que emitió tu tarjeta.",
    usdBank: "¿Tu tarjeta fue emitida por un banco estadounidense?",
    usdAction: "Paga en dólares · USD",
    mxnBank: "¿Tu tarjeta fue emitida por un banco mexicano?",
    mxnAction: "Paga en pesos · MXN",
    currencyHelp: "Usa el país del banco emisor de tu tarjeta, no tu ubicación actual. Los precios de abajo se actualizan a la moneda seleccionada.",
    names: { day: "Pase de día", week: "Pase semanal", full: "Pase completo" } as Record<PassKey, string>,
    scope: { day: "Cualquier día", week: "Una semana temática", full: "Las cuatro semanas" } as Record<PassKey, string>,
    desc: {
      day: "Elige tu día cuando se cierre el calendario. Acceso completo a las ofertas de ese día.",
      week: "Siete días de una semana temática: cenas, sesiones y talleres incluidos.",
      full: "Todo, todo el mes. Acceso a las cuatro semanas temáticas.",
    } as Record<PassKey, string>,
    buy: { day: "Comprar pase de día", week: "Comprar pase semanal", full: "Comprar pase completo" } as Record<PassKey, string>,
    buying: "Redirigiendo…", signInBuy: "Inicia sesión y compra", soldOut: "Agotado",
    bestValue: "Mejor valor",
    octPrice: "precio de octubre", fromSep: "desde el 1 de septiembre",
    passNote: "La tarifa de agosto es 50% menos que el precio de octubre y sube el 1 de septiembre. Tu pase cubre las fechas del festival, sin importar cuándo lo compres.",
    ladderEyebrow: "El precio solo sube",
    ladder: [
      { mo: "Agosto", off: "50% de descuento", now: true },
      { mo: "Septiembre", off: "25% de descuento", now: false },
      { mo: "Octubre", off: "Precio completo", now: false },
    ],
    nowTag: "Ahora",
    ladderNote: "Compra hoy y conservas la tarifa de agosto. Es un solo pago, no hay nada que renovar.",
    weeksEyebrow: "Cuatro semanas, cuatro temas",
    weeks: [
      { t: "Una celebración de los destilados", d: "28 oct – 3 nov" },
      { t: "Lo mejor de la cocina mexicana", d: "4 – 10 nov" },
      { t: "Delicias internacionales", d: "11 – 17 nov" },
      { t: "Gratitud por la comida del mundo", d: "18 – 27 nov" },
    ],
    week: "Semana ",
    closerTitle: "Asegura la tarifa de agosto",
    closerLede: "El 50% de descuento termina el 31 de agosto. Después, el mismo pase cuesta 25% más.",
    stickyT: "Termina el 31 de agosto", stickyCta: "Comprar",
    stickyFrom: "Desde",
    footFine: "Eat With Me presenta Taste of Tulum, Food & Wine Festival · Tulum, Quintana Roo, México",
    footCurrency: "Los precios se muestran en la moneda de pago seleccionada arriba.",
    heroAlt: "La Catrina coronada de cempasúchil sobre la costa de Tulum al atardecer",
    panAlt: "Patios de restaurante en Tulum iluminados con farolillos al atardecer",
    left: "restantes",
    payFailed: "No se pudo iniciar el pago. Intenta de nuevo.",
  },
};
type Copy = typeof T.en;

/* ---------------------------------------------------------------- bits */

const PATH: Record<string, string> = {
  check: "M5 12.5l4.5 4.5L19 7",
  lock: "M7 10.5V7.5a5 5 0 0 1 10 0v3M5.5 10.5h13v10h-13z",
  globe: "M3 12h18M12 3c2.5 2.5 2.5 16 0 18M12 3c-2.5 2.5-2.5 16 0 18",
  clock: "M12 7.5V12l3 2",
  arrow: "M4 12h15m0 0-5.5-5.5M19 12l-5.5 5.5",
};
function Ico({ n, s = 18, w = 1.7 }: { n: string; s?: number; w?: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {(n === "globe" || n === "clock") ? <circle cx="12" cy="12" r="9" /> : null}
      <path d={PATH[n]} />
    </svg>
  );
}

function useCountdown(target: Date) {
  const [ms, setMs] = useState<number>(() => target.getTime() - Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setMs(target.getTime() - Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [target]);
  if (ms <= 0) return null;
  return {
    d: Math.floor(ms / 864e5),
    h: Math.floor(ms / 36e5) % 24,
    m: Math.floor(ms / 6e4) % 60,
  };
}

function fmt(cents: number): string {
  const v = cents / 100;
  return Number.isInteger(v) ? `$${v.toLocaleString("en-US")}` : `$${v.toFixed(2)}`;
}
function tierByPrefix(p: Product, prefix: string): Tier | null {
  return p.all_tiers.find((t) => t.code.startsWith(prefix)) ?? null;
}
/** "150 US dollars" for the aria-label wrapping a formatted display + currency code. */
function ariaPrice(display: string, cur: string): string {
  return `${display.replace(/^\$/, "")} ${currencyName(cur)}`;
}

/* ---------------------------------------------------------------- page */

function SalesLanding() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [lang, setLang] = useState<"en" | "es">(() =>
    (typeof localStorage !== "undefined" && localStorage.getItem("tot26-lang") === "es") ? "es" : "en");
  const [currency, setCurrency] = useState<string>(() =>
    (typeof localStorage !== "undefined" && localStorage.getItem("tot26-cur")) || "USD");
  const [catalog, setCatalog] = useState<CatalogResp | null>(null);
  const [buying, setBuying] = useState<PassKey | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [showBar, setShowBar] = useState(false);
  const heroRef = useRef<HTMLDivElement | null>(null);

  const t: Copy = T[lang];

  useEffect(() => {
    localStorage.setItem("tot26-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  useEffect(() => { localStorage.setItem("tot26-cur", currency); }, [currency]);

  useEffect(() => {
    let cancelled = false;
    api<CatalogResp>(`/api/v1/pass-catalog?currency=${encodeURIComponent(currency)}`)
      .then((d) => { if (!cancelled) setCatalog(d); })
      .catch(() => { /* prices fall back to prototype statics below */ });
    return () => { cancelled = true; };
  }, [currency]);

  useEffect(() => {
    const el = heroRef.current;
    if (!el || !window.IntersectionObserver) return;
    const io = new IntersectionObserver((es) => setShowBar(!es[0]!.isIntersecting), { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // product lookup by key; static fallback keeps the page render-safe if the
  // API hiccups (USD prototype numbers).
  const FALLBACK: Record<string, Record<PassKey, { now: string; oct: string; sep: string }>> = {
    USD: {
      day: { now: "$10", oct: "$20", sep: "$15" },
      week: { now: "$38", oct: "$75", sep: "$56" },
      full: { now: "$100", oct: "$200", sep: "$150" },
    },
    MXN: {
      day: { now: "$200", oct: "$400", sep: "$300" },
      week: { now: "$760", oct: "$1,520", sep: "$1,140" },
      full: { now: "$2,000", oct: "$4,000", sep: "$3,000" },
    },
  };
  function passData(key: PassKey) {
    const activeCatalog = catalog?.currency === currency ? catalog : null;
    const product = activeCatalog?.products.find((p) => PRODUCT_KEY[p.name] === key) ?? null;
    const cur = product?.current_tier ?? null;
    const oct = product ? (tierByPrefix(product, "OCTOBER") ?? null) : null;
    const sep = product ? (tierByPrefix(product, "SEPTEMBER") ?? null) : null;
    const fallback = FALLBACK[currency]?.[key] ?? FALLBACK.USD[key];
    return {
      product,
      now: cur ? fmt(cur.price_cents) : fallback.now,
      oct: oct ? fmt(oct.price_cents) : fallback.oct,
      sep: sep ? fmt(sep.price_cents) : fallback.sep,
      soldOut: (product?.inventory_remaining ?? 1) <= 0,
    };
  }
  const dayFrom = passData("day").now;

  async function onBuy(key: PassKey) {
    setPayError(null);
    const { product } = passData(key);
    if (!product?.current_tier) return;
    if (!user) {
      savePostAuthTarget("/passes");
      void navigate({ to: "/login" as never, search: { returnTo: "/passes" } as never });
      return;
    }
    setBuying(key);
    try {
      const resp = await api<{ url: string }>("/api/v1/checkout/session", {
        method: "POST",
        json: { pricing_tier_id: product.current_tier.id, quantity: 1 },
        idempotencyKey: newIdempotencyKey(),
      });
      window.location.assign(resp.url);
    } catch (e) {
      setBuying(null);
      setPayError(e instanceof ApiError ? (e.problem.detail ?? t.payFailed) : t.payFailed);
    }
  }

  const c = useCountdown(STEP_UP);
  const currencies = catalog?.available_currencies?.length ? catalog.available_currencies : ["USD", "MXN"];

  return (
    <div className="lp" data-screen-label="Sales landing v2">
      {/* banner */}
      <div className="banner">
        <b>{t.bannerOffer}</b>
        <span className="sep">{t.bannerStep}</span>
        {c ? (
          <span className="cd tnum">
            <Ico n="clock" s={13} w={1.9} />
            {c.d}d {String(c.h).padStart(2, "0")}h {String(c.m).padStart(2, "0")}m {t.left}
          </span>
        ) : null}
      </div>

      {/* nav */}
      <div className="nav">
        <div className="in">
          <span className="lock">
            <span className="side">
              <img src="/assets/eatwithme-white.png" alt="Eat With Me" />
              <span className="cap">{lang === "es" ? "presenta" : "presents"}</span>
            </span>
            <span className="rule"></span>
            <img className="mark" src="/assets/tulum-logo-gold.png" alt="Taste of Tulum, Food & Wine Festival" />
            <span className="rule"></span>
            <span className="side">
              <span className="cap">by</span>
              <img src="/assets/awm-white.png" alt="Art With Me" />
            </span>
          </span>
          <span className="navr">
            <button className="lang" type="button" title={t.langTitle} onClick={() => setLang(lang === "en" ? "es" : "en")}>
              <Ico n="globe" s={15} w={1.6} />{t.lang}
            </button>
            <a className="cta cta--ghost" href="#passes">{t.navBuy}</a>
          </span>
        </div>
      </div>

      {/* hero */}
      <div ref={heroRef}>
        <div className="hero">
          <img src="/assets/hero-bg.jpg" alt={t.heroAlt} />
          <div className="scrim"></div>
          <div className="in">
            <div>
              <div className="eyebrow on-dark">{t.heroEyebrow}</div>
              <h1>{t.h1}</h1>
              <div className="script">{t.script}</div>
              <p className="lede">{t.lede}</p>
              <div className="anchor">
                <span className="from">{t.from}</span>
                <span aria-label={ariaPrice(dayFrom, currency)}>
                  <span className="big" aria-hidden="true">{dayFrom}</span>
                  <span className="cur" aria-hidden="true">{" "}{currency}</span>
                </span>
              </div>
              <div className="actions">
                <a className="cta" href="#passes">{t.buyNow}</a>
                <a className="cta cta--ghost" href="#included">{t.seeIncluded}</a>
              </div>
              <p className="trust"><Ico n="lock" s={15} w={1.8} />{t.trust}</p>
            </div>
            <div className="hcard" id="included">
              <div className="eyebrow">{t.inclEyebrow}</div>
              <h2 className="hct">{t.inclTitle}</h2>
              <ul className="checks">
                {t.incl.map((s, i) => <li key={i}><span className="tick"><Ico n="check" s={13} w={2.8} /></span>{s}</li>)}
              </ul>
              <p className="hcfine">{t.fromNote}</p>
            </div>
          </div>
        </div>
      </div>

      {/* passes */}
      <section className="pan" id="passes">
        <img src="/assets/restaurant-scene.jpg" alt={t.panAlt} />
        <div className="scrim"></div>
        <div className="frame"></div>
        <div className="in">
          <div className="head">
            <div className="eyebrow on-dark">{t.passEyebrow}</div>
            <h2 className="h2 on-dark">{t.passTitle}</h2>
          </div>
          <div className="issuer-choice" role="group" aria-label={t.currencyTitle}>
            <div className="issuer-choice__intro">
              <div className="eyebrow">{t.currencyEyebrow}</div>
              <h3>{t.currencyTitle}</h3>
            </div>
            <div className="issuer-choice__options">
              {currencies.includes("USD") && (
                <button type="button" data-on={currency === "USD" ? "1" : "0"} onClick={() => setCurrency("USD")}>
                  <span>{t.usdBank}</span>
                  <strong>{t.usdAction}</strong>
                </button>
              )}
              {currencies.includes("MXN") && (
                <button type="button" data-on={currency === "MXN" ? "1" : "0"} onClick={() => setCurrency("MXN")}>
                  <span>{t.mxnBank}</span>
                  <strong>{t.mxnAction}</strong>
                </button>
              )}
            </div>
            <p>{t.currencyHelp}</p>
          </div>
          {payError && <p className="payerr">{payError}</p>}
          <div className="passes">
            {(["day", "week", "full"] as PassKey[]).map((key) => {
              const d = passData(key);
              const hero = key === "full";
              return (
                <div className={"card" + (hero ? " card--hero" : "")} key={key}>
                  {hero ? <span className="flag">{t.bestValue}</span> : null}
                  <div className="nm">{t.names[key]}</div>
                  <div className="sc">{t.scope[key]}</div>
                  <div className="price">
                    <span aria-label={ariaPrice(d.now, currency)}>
                      <span className="amt" aria-hidden="true">{d.now}</span>
                      <span className="cur" aria-hidden="true">{" "}{currency}</span>
                    </span>
                  </div>
                  <div className="was"><s>{d.oct}</s>{t.octPrice}</div>
                  <span className="step" aria-label={`${d.sep.replace(/^\$/, "")} ${currencyName(currency)} ${t.fromSep}`}>
                    {d.sep}{" "}
                    <span className="cur" aria-hidden="true">{currency}</span> {t.fromSep}
                  </span>
                  <p className="ds">{t.desc[key]}</p>
                  <button
                    className="cta cta--teal"
                    type="button"
                    disabled={d.soldOut || buying === key || authLoading}
                    onClick={() => void onBuy(key)}
                  >
                    {d.soldOut ? t.soldOut : buying === key ? t.buying : user ? t.buy[key] : t.signInBuy}
                  </button>
                </div>
              );
            })}
          </div>
          <p className="note on-dark">{t.passNote}</p>
        </div>
      </section>

      {/* ladder */}
      <section className="sec">
        <div className="in">
          <div className="flourish"></div>
          <div className="eyebrow">{t.ladderEyebrow}</div>
          <div className="ladder">
            {t.ladder.map((r) => (
              <div className="rung" key={r.mo} data-now={r.now ? "1" : "0"}>
                <s className="dot"></s>
                <span><span className="mo">{r.mo}</span><span className="off" style={{ display: "block" }}>{r.off}</span></span>
                {r.now ? <span className="now">{t.nowTag}</span> : <span></span>}
              </div>
            ))}
          </div>
          <p className="note">{t.ladderNote}</p>
        </div>
      </section>

      {/* weeks */}
      <section className="sec" style={{ paddingTop: 0 }}>
        <div className="in">
          <div className="eyebrow">{t.weeksEyebrow}</div>
          <div className="weeks">
            {t.weeks.map((w, i) => (
              <div className="wk" key={i}>
                <div className="n"><s style={{ background: WEEK_C[i] }}></s>{t.week + (i + 1)}</div>
                <div className="t">{w.t}</div>
                <div className="d">{w.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* closer */}
      <div className="closer">
        <div className="in">
          <div className="eyebrow on-dark">{t.bannerOffer}</div>
          <h2 className="h2 on-dark">{t.closerTitle}</h2>
          <p className="lede">{t.closerLede}</p>
          <a className="cta" href="#passes">{t.buyNow}</a>
          <p className="trust" style={{ justifyContent: "center" }}><Ico n="lock" s={15} w={1.8} />{t.trust}</p>
        </div>
      </div>

      {/* footer */}
      <div className="foot">
        <div className="in">
          <img src="/assets/logo-cream.png" alt="Taste of Tulum, Food & Wine Festival" />
          <p className="fine">{t.footFine}</p>
          <p className="fine">{t.footCurrency}</p>
        </div>
      </div>
      <div className="tail"></div>

      {/* sticky buy bar (mobile) */}
      <div className="sticky" data-show={showBar ? "1" : "0"}>
        <div>
          <div className="t">{t.stickyT}</div>
          <div className="s">
            {t.stickyFrom}{" "}
            <span aria-label={ariaPrice(dayFrom, currency)}>
              <span aria-hidden="true">{dayFrom}</span>
              <span className="cur" aria-hidden="true">{" "}{currency}</span>
            </span>
          </div>
        </div>
        <a className="cta" href="#passes">{t.stickyCta}<Ico n="arrow" s={15} w={2.1} /></a>
      </div>
    </div>
  );
}
